import React from "react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Megaphone, Heart } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function AnalyticsPanel({ analytics }) {
  const { t } = useLang();
  if (!analytics) return null;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card className="p-4 border-border/60">
        <h3 className="font-heading text-lg mb-3">{t("ad.analytics_by_city")}</h3>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={analytics.by_city}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="city" hide />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card className="p-4 border-border/60">
        <h3 className="font-heading text-lg mb-3">{t("ad.analytics_by_category")}</h3>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={analytics.by_category}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="category" hide />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card className="p-4 border-border/60 md:col-span-2">
        <h3 className="font-heading text-lg mb-3">{t("ad.analytics_top")}</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("ad.col_business")}</TableHead>
              <TableHead>{t("ad.col_category")}</TableHead>
              <TableHead>{t("ad.col_city")}</TableHead>
              <TableHead>{t("ad.col_rating")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {analytics.top_vendors.map((v) => (
              <TableRow key={v.vendor_id}>
                <TableCell>{v.business_name}</TableCell>
                <TableCell>{v.category}</TableCell>
                <TableCell>{v.city}</TableCell>
                <TableCell>{v.rating}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-4 border-border/60" data-testid="admin-analytics-inquiries">
        <h3 className="font-heading text-lg mb-3 inline-flex items-center gap-2"><Megaphone className="h-4 w-4" /> Inquiries breakdown</h3>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={[
              { label: "Active", count: analytics.active_inquiries ?? 0 },
              { label: "Expired", count: analytics.expired_inquiries ?? 0 },
              { label: "Closed", count: analytics.closed_inquiries ?? 0 },
              { label: "Replies", count: analytics.total_inquiry_replies ?? 0 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#f59e0b" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-4 border-border/60" data-testid="admin-analytics-matrimonial">
        <h3 className="font-heading text-lg mb-3 inline-flex items-center gap-2"><Heart className="h-4 w-4" /> Matrimonial by marital status</h3>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={[
              ...(analytics.matrimonial_by_status || []).map((r) => ({ label: r.status, count: r.count })),
              { label: "Manglik", count: analytics.manglik_count ?? 0 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#a855f7" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
